/**
 * find_risks — safe agent tool. Walks the diagram-data and returns
 * nodes tagged as risks, with linked processes/controls when present.
 *
 * Pure client-side over `context.diagramData` — no fetch, instant.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';
import type { Node, Edge } from '../../../../core/models/scene';

interface RiskRow {
  id: string;
  label: string;
  severity?: string;
  likelihood?: string;
  linkedProcesses: string[];
  linkedControls: string[];
}

function readString(props: Record<string, unknown>, key: string): string | undefined {
  const v = props[key];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function isRiskNode(node: Node): boolean {
  if (node.type === 'risk') return true;
  const props = (node.props ?? {}) as Record<string, unknown>;
  if (props.subtype === 'risk') return true;
  return Array.isArray((node as unknown as { tags?: string[] }).tags)
    ? Boolean((node as unknown as { tags?: string[] }).tags?.includes('risk'))
    : false;
}

/**
 * Universal-graph nodes carry their display string on `label`, but
 * `core/models/scene.Node` (genogram-shaped) only has `name`. The
 * agent's diagram data feeds a mix of both, so read both fields and
 * fall back to the id. Cast once here instead of leaking ad-hoc casts
 * across every map().
 */
function labelOf(node: Node): string {
  const n = node as unknown as { label?: string; name?: string };
  return n.label ?? n.name ?? node.id;
}

export const findRisksTool: IAgentTool = {
  name: 'find_risks',
  description:
    'List risk nodes in the current folder graph along with linked processes and controls. Use when the user asks to flag risks, find risks, or audit risk coverage.',
  parameters: {
    type: 'object',
    properties: {
      severity: {
        type: 'string',
        description: 'Optional. Filter to a single severity (high|medium|low).',
      },
      minLinkedProcesses: {
        type: 'number',
        description: 'Optional. Only return risks with at least this many linked processes.',
      },
    },
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'search',

  async execute(params, context: IAgentToolContext): Promise<AgentToolResult> {
    const nodes = context.diagramData.nodes as Node[];
    const edges = context.diagramData.edges as Edge[];

    const nodeById = new Map(nodes.map((n) => [n.id, n]));
    const risks = nodes.filter(isRiskNode);

    const rows: RiskRow[] = risks.map((risk) => {
      const props = (risk.props ?? {}) as Record<string, unknown>;
      const linkedProcesses: string[] = [];
      const linkedControls: string[] = [];
      for (const edge of edges) {
        if (edge.from === risk.id || edge.to === risk.id) {
          const otherId = edge.from === risk.id ? edge.to : edge.from;
          const other = nodeById.get(otherId);
          if (!other) continue;
          if (other.type === 'process') linkedProcesses.push(labelOf(other));
          else if (other.type === 'control') linkedControls.push(labelOf(other));
        }
      }
      return {
        id: risk.id,
        label: labelOf(risk),
        severity: readString(props, 'severity'),
        likelihood: readString(props, 'likelihood'),
        linkedProcesses,
        linkedControls,
      };
    });

    const severityFilter = typeof params.severity === 'string' ? params.severity.toLowerCase() : null;
    const minProcs =
      typeof params.minLinkedProcesses === 'number' ? params.minLinkedProcesses : 0;
    const filtered = rows.filter((r) => {
      if (severityFilter && (r.severity ?? '').toLowerCase() !== severityFilter) return false;
      if (r.linkedProcesses.length < minProcs) return false;
      return true;
    });

    if (filtered.length === 0) {
      return {
        success: true,
        summary: 'No risk nodes match the requested filter in this folder.',
        data: { risks: [] },
      };
    }

    const lines = filtered
      .slice(0, 12)
      .map((r) => {
        const sev = r.severity ? ` [${r.severity}]` : '';
        const procs = r.linkedProcesses.length > 0 ? `; processes: ${r.linkedProcesses.slice(0, 3).join(', ')}` : '';
        const ctrls = r.linkedControls.length > 0 ? `; controls: ${r.linkedControls.slice(0, 3).join(', ')}` : '';
        return `- ${r.label}${sev}${procs}${ctrls}`;
      })
      .join('\n');

    return {
      success: true,
      summary: `Found ${filtered.length} risk(s) in this folder.\n${lines}`,
      data: { risks: filtered },
      createdResourceIds: filtered.map((r) => r.id),
      uiSurface: 'chat',
    };
  },
};
